import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/utils/supabaseClient';
import { signUpWithPublicEmail } from '@/lib/publicEmailSignup.js';
import { STAFF_RESET_PATH } from '../../shared/publicationAccess.js';

const isDevEnvironment = Boolean(import.meta.env?.DEV);
const isBrowserEnvironment = typeof window !== 'undefined';
const RETIRED_ADMIN_PREVIEW_STORAGE_KEY = 'admin:previewOverrides';
const isDevAuthFallbackDisabled = ['true', '1', 'on', 'yes'].includes(
  String(import.meta.env?.VITE_DISABLE_DEV_AUTH_FALLBACK || '')
    .trim()
    .toLowerCase(),
);

const getProviderLabel = (credentials) => {
  if (credentials?.provider && typeof credentials.provider === 'string') {
    return `oauth:${credentials.provider}`;
  }
  return 'password';
};

const hashEmailForTelemetry = async (email) => {
  if (!isBrowserEnvironment) {
    return null;
  }
  if (typeof email !== 'string') {
    return null;
  }
  const normalized = email.trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  const subtle = window.crypto?.subtle;
  if (!subtle) {
    return null;
  }
  try {
    const encoder = new TextEncoder();
    const digest = await subtle.digest('SHA-256', encoder.encode(normalized));
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch (error) {
    console.warn('[AuthContext] Unable to hash email for telemetry', error);
    return null;
  }
};

const sendSignInTelemetry = async ({
  status,
  provider,
  emailHash = null,
  reason = null,
  accessToken = null,
} = {}) => {
  if (!isBrowserEnvironment) {
    return;
  }
  if (typeof status !== 'string' || status.trim().length === 0) {
    return;
  }

  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const payloadEntries = [
    ['status', status.trim().toLowerCase()],
    ['provider', provider || 'password'],
    ['emailHash', emailHash],
    ['reason', reason],
  ].filter(([, value]) => value !== null && value !== undefined && value !== '');

  const body = JSON.stringify(Object.fromEntries(payloadEntries));

  try {
    await fetch('/api/auth/monitoring/sign-in', {
      method: 'POST',
      headers,
      body,
      keepalive: status === 'failure',
    });
  } catch (error) {
    console.warn('[AuthContext] sign-in telemetry dispatch failed', error);
  }
};

const FALLBACK_BASE_USER = {
  id: '00000000-0000-4000-8000-000000000001',
  email: 'dev@example.com',
  full_name: 'Dev User',
  email_confirmed_at: null,
  email_verified: false,
  role: 'admin',
  avatar_url: null,
  avatar_type: 'generated',
  avatar_seed: 'fallback-seed',
};

// In production we treat missing role metadata as a regular user.
// The admin role must be explicitly granted via server-controlled Supabase app_metadata.
const DEFAULT_PRODUCTION_ROLE = 'user';

const FALLBACK_PERMISSIONS = [
  'feature_flags.manage',
  'content.ingest',
  'gamification.missions.manage',
  'commerce.manage',
  'telemetry.export',
  'privacy.dsr.manage',
  'security.audit',
  'cron.manage',
  'support.manage',
  'social.moderate',
  'streams.trigger',
  'rbac.manage',
];

const PROFILE_TRACK_TYPES = ['social', 'librarian', 'streamer'];
const USER_PROFILE_COLUMNS = [
  'user_id',
  'base_tier',
  'gamer_tier',
  'gamer_multiplier',
  'gamer_since',
  'gamer_expires_at',
  'social_unlocked',
  'social_unlocked_at',
  'librarian_unlocked',
  'librarian_unlocked_at',
  'streamer_unlocked',
  'streamer_unlocked_at',
  'customer_access_enabled',
  'customer_override',
  'customer_unlocked_at',
  'free_badge_state',
  'metadata',
];
const USER_PROFILE_TRACK_COLUMNS = [
  'id',
  'track_type',
  'status',
  'xp_earned',
  'missions_total',
  'missions_completed',
  'progress_summary',
  'last_mission_completed_at',
  'completed_at',
  'updated_at',
];
const USER_PROFILE_SELECT = USER_PROFILE_COLUMNS.join(', ');
const USER_PROFILE_TRACK_SELECT = USER_PROFILE_TRACK_COLUMNS.join(', ');

const IDLE_TIMEOUT_MINUTES =
  Number.parseInt(import.meta.env?.VITE_IDLE_TIMEOUT_MINUTES || '', 10) || 30;
const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_MINUTES * 60 * 1000;

const getE2EPreset = () => {
  if (!isDevEnvironment) {
    return null;
  }
  if (typeof window === 'undefined') {
    return null;
  }
  const preset = window.__E2E_AUTH__;
  if (!preset || typeof preset !== 'object') {
    return null;
  }

  const normalizedPermissions = Array.isArray(preset.permissions)
    ? preset.permissions
        .map((value) => (typeof value === 'string' ? value.trim() : null))
        .filter(Boolean)
    : null;

  const normalizedMetadata =
    preset.metadata && typeof preset.metadata === 'object' && preset.metadata !== null
      ? preset.metadata
      : {};
  const accessToken =
    typeof preset.accessToken === 'string' && preset.accessToken.trim().length > 0
      ? preset.accessToken.trim()
      : null;

  return {
    role: typeof preset.role === 'string' ? preset.role.trim() : null,
    tier: typeof preset.tier === 'string' ? preset.tier.trim() : null,
    permissions: normalizedPermissions,
    metadata: normalizedMetadata,
    accessToken,
  };
};

const shouldUseDevFallback = () => {
  if (!isDevEnvironment || isDevAuthFallbackDisabled) {
    return false;
  }
  if (!isBrowserEnvironment) {
    return true;
  }
  return window.__E2E_DISABLE_FALLBACK__ !== true;
};

const coerceArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const buildTrackSnapshotFromRow = (row) => {
  if (!row) {
    return {
      status: 'locked',
      unlocked: false,
      completed: false,
      completed_at: null,
      last_mission_completed_at: null,
      xp_earned: 0,
      missions_total: 0,
      missions_completed: 0,
      progress: null,
    };
  }

  const status = row.status || 'locked';
  return {
    id: row.id || null,
    track_type: row.track_type || null,
    status,
    unlocked: status !== 'locked',
    completed: status === 'complete',
    completed_at: row.completed_at || null,
    last_mission_completed_at: row.last_mission_completed_at || null,
    xp_earned: row.xp_earned || 0,
    missions_total: row.missions_total || 0,
    missions_completed: row.missions_completed || 0,
    progress: row.progress_summary || null,
    updated_at: row.updated_at || null,
  };
};

const buildProfileSnapshotPayload = (profileRow, trackRows = []) => {
  if (!profileRow) {
    return null;
  }

  const tracks = PROFILE_TRACK_TYPES.reduce((acc, trackType) => {
    const entry = trackRows.find((row) => row.track_type === trackType);
    acc[trackType] = buildTrackSnapshotFromRow(entry);
    return acc;
  }, {});

  return {
    user_id: profileRow.user_id,
    base_tier: profileRow.base_tier || null,
    gamer_tier: profileRow.gamer_tier || null,
    gamer_multiplier: profileRow.gamer_multiplier || null,
    gamer_since: profileRow.gamer_since || null,
    gamer_expires_at: profileRow.gamer_expires_at || null,
    social_unlocked: Boolean(profileRow.social_unlocked),
    social_unlocked_at: profileRow.social_unlocked_at || null,
    librarian_unlocked: Boolean(profileRow.librarian_unlocked),
    librarian_unlocked_at: profileRow.librarian_unlocked_at || null,
    streamer_unlocked: Boolean(profileRow.streamer_unlocked),
    streamer_unlocked_at: profileRow.streamer_unlocked_at || null,
    customer_access_enabled: Boolean(profileRow.customer_access_enabled),
    customer_override: Boolean(profileRow.customer_override),
    customer_unlocked_at: profileRow.customer_unlocked_at || null,
    badges: coerceArrayValue(profileRow.free_badge_state || []),
    tracks,
    metadata: profileRow.metadata || {},
  };
};

const isProfileSchemaUnavailable = (error) => {
  if (!error) {
    return false;
  }
  const code = error.code || error.details;
  if (code && (code === '42P01' || code === '42501')) {
    return true;
  }
  const message =
    typeof error.message === 'string' ? error.message.toLowerCase() : '';
  if (!message) {
    return false;
  }
  return (
    message.includes('user_profiles') || message.includes('user_profile_tracks')
  );
};

export const fetchUserProfileSnapshot = async (userId) => {
  if (!userId || shouldUseDevFallback()) {
    return null;
  }
  try {
    const [profileResult, trackResult] = await Promise.all([
      supabase
        .from('user_profiles')
        .select(USER_PROFILE_SELECT)
        .eq('user_id', userId)
        .limit(1),
      supabase
        .from('user_profile_tracks')
        .select(USER_PROFILE_TRACK_SELECT)
        .eq('user_id', userId),
    ]);

    if (profileResult.error) {
      if (isProfileSchemaUnavailable(profileResult.error)) {
        return null;
      }
      throw profileResult.error;
    }
    if (trackResult.error) {
      if (isProfileSchemaUnavailable(trackResult.error)) {
        return null;
      }
      throw trackResult.error;
    }

    const profileRow = Array.isArray(profileResult.data)
      ? profileResult.data[0]
      : profileResult.data;
    if (!profileRow) {
      return null;
    }
    const trackRows = Array.isArray(trackResult.data) ? trackResult.data : [];
    return buildProfileSnapshotPayload(profileRow, trackRows);
  } catch (error) {
    if (!isProfileSchemaUnavailable(error)) {
      console.warn('[AuthContext] fetchUserProfileSnapshot failed', error);
    }
    return null;
  }
};

export const mergeUserProfileSnapshotMetadata = (user, snapshot) => {
  if (!user || !snapshot) {
    return user;
  }

  const baseMetadata = { ...(user.metadata || {}) };
  delete baseMetadata.profile_tracks;
  delete baseMetadata.profileTracks;

  const baseAppMetadata = { ...(user.app_metadata || {}) };
  delete baseAppMetadata.profile_tracks;
  delete baseAppMetadata.profileTracks;

  const nextMetadata = {
    ...baseMetadata,
    base_tier: snapshot.base_tier || baseMetadata.base_tier,
    gamer_tier: snapshot.gamer_tier || baseMetadata.gamer_tier,
    gamer_multiplier: snapshot.gamer_multiplier || baseMetadata.gamer_multiplier,
    gamer_since: snapshot.gamer_since || baseMetadata.gamer_since,
    gamer_expires_at: snapshot.gamer_expires_at || baseMetadata.gamer_expires_at,
    social_unlocked: snapshot.social_unlocked,
    librarian_unlocked: snapshot.librarian_unlocked,
    streamer_unlocked: snapshot.streamer_unlocked,
    customer_access_enabled: snapshot.customer_access_enabled,
    customer_override: snapshot.customer_override,
    customer_unlocked_at: snapshot.customer_unlocked_at,
    free_badge_state: snapshot.badges,
    profile_snapshot: snapshot,
  };
  const nextAppMetadata = {
    ...baseAppMetadata,
    base_tier: nextMetadata.base_tier,
    gamer_tier: snapshot.gamer_tier || baseAppMetadata.gamer_tier,
    gamer_multiplier: snapshot.gamer_multiplier || baseAppMetadata.gamer_multiplier,
    profile_snapshot: snapshot,
    customer_access_enabled: snapshot.customer_access_enabled,
  };
  const nextRaw = user.raw
    ? {
        ...user.raw,
        user_metadata: {
          ...(() => {
            const payload = { ...(user.raw.user_metadata || {}) };
            delete payload.profile_tracks;
            delete payload.profileTracks;
            return payload;
          })(),
          ...nextMetadata,
        },
        app_metadata: {
          ...(() => {
            const payload = { ...(user.raw.app_metadata || {}) };
            delete payload.profile_tracks;
            delete payload.profileTracks;
            return payload;
          })(),
          ...nextAppMetadata,
        },
      }
    : user.raw;
  return {
    ...user,
    metadata: nextMetadata,
    app_metadata: nextAppMetadata,
    raw: nextRaw,
  };
};

export const hydrateUserProfileSnapshot = async (user) => {
  if (!user?.id || shouldUseDevFallback()) {
    return user;
  }
  const snapshot = await fetchUserProfileSnapshot(user.id);
  if (!snapshot) {
    return user;
  }
  return mergeUserProfileSnapshotMetadata(user, snapshot);
};

const getDevFallbackPermissions = () => [
  ...(getE2EPreset()?.permissions || FALLBACK_PERMISSIONS),
];

const applyE2EOverrides = (user) => {
  if (!isDevEnvironment || typeof window === 'undefined') {
    return user;
  }
  const preset = getE2EPreset();
  if (!preset || !user) {
    return user;
  }

  const metadata = {
    ...(user.metadata || {}),
    ...(preset.metadata || {}),
  };

  if (preset.tier) {
    metadata.tier = preset.tier;
  }

  return {
    ...user,
    role: preset.role || user.role,
    metadata,
    app_metadata: {
      ...(user.app_metadata || {}),
      role: preset.role || user.app_metadata?.role || user.role,
    },
  };
};

const clearRetiredAdminPreviewState = () => {
  if (!isBrowserEnvironment) {
    return;
  }
  try {
    window.localStorage?.removeItem(RETIRED_ADMIN_PREVIEW_STORAGE_KEY);
  } catch (cleanupError) {
    console.warn('[AuthContext] Unable to clear retired admin preview state', cleanupError);
  }
};

export const getFallbackUser = () => ({
  ...FALLBACK_BASE_USER,
  role: getE2EPreset()?.role || FALLBACK_BASE_USER.role,
  metadata: {
    tier: getE2EPreset()?.tier || 'registered',
    ...(getE2EPreset()?.metadata || {}),
  },
  app_metadata: {
    role: getE2EPreset()?.role || FALLBACK_BASE_USER.role,
  },
  created_date: new Date().toISOString(),
});

export const mapSupabaseUserToProfile = (supabaseUser) => {
  if (!supabaseUser) {
    return null;
  }

  const metadata = supabaseUser.user_metadata || {};
  const appMetadata = supabaseUser.app_metadata || {};
  const rawFullName = metadata.full_name || metadata.name || metadata.display_name;
  const derivedFullName = rawFullName || supabaseUser.email?.split('@')[0] || FALLBACK_BASE_USER.full_name;
  const emailConfirmedAt =
    supabaseUser.email_confirmed_at ||
    supabaseUser.confirmed_at ||
    null;

  const profile = {
    id: supabaseUser.id,
    email: supabaseUser.email || FALLBACK_BASE_USER.email,
    full_name: derivedFullName,
    display_name: metadata.display_name || derivedFullName,
    email_confirmed_at: emailConfirmedAt,
    email_verified: Boolean(emailConfirmedAt),
    role: appMetadata.role || DEFAULT_PRODUCTION_ROLE,
    avatar_url: metadata.avatar_url || metadata.avatarUrl || FALLBACK_BASE_USER.avatar_url,
    avatar_type: metadata.avatar_type || metadata.avatarType || FALLBACK_BASE_USER.avatar_type,
    avatar_seed: metadata.avatar_seed || metadata.avatarSeed || FALLBACK_BASE_USER.avatar_seed,
    created_date: supabaseUser.created_at || new Date().toISOString(),
    metadata,
    app_metadata: appMetadata,
    raw: supabaseUser,
  };

  return applyE2EOverrides(profile);
};

let externalAuthHandlers = {
  async signIn() {
    throw new Error('AuthProvider is not mounted yet. Ensure AuthProvider wraps your component tree.');
  },
  async signUp() {
    throw new Error('AuthProvider is not mounted yet. Ensure AuthProvider wraps your component tree.');
  },
  async signOut() {
    throw new Error('AuthProvider is not mounted yet. Ensure AuthProvider wraps your component tree.');
  },
};

export const setExternalAuthHandlers = (handlers) => {
  externalAuthHandlers = handlers;
};

export const getExternalAuthHandlers = () => externalAuthHandlers;

export const assertIdentityLinkSession = async (supabaseClient, expectedUserId) => {
  if (!expectedUserId) {
    const error = new Error('link_identity_requires_session');
    error.code = 'link_identity_requires_session';
    error.status = 401;
    throw error;
  }

  const { data, error: getUserError } = await supabaseClient.auth.getUser();
  if (getUserError || !data?.user || data.user.id !== expectedUserId) {
    const error = new Error('link_identity_invalid_session');
    error.code = 'link_identity_invalid_session';
    error.status = 401;
    throw error;
  }

  return data.user;
};

const AuthContext = createContext({
  session: null,
  user: shouldUseDevFallback() ? getFallbackUser() : null,
  isLoading: true,
  error: null,
  signIn: externalAuthHandlers.signIn,
  signUp: externalAuthHandlers.signUp,
  recordAuthSignupCompleted: async () => ({
    attempted: false,
    reason: 'auth_provider_not_mounted',
  }),
  signOut: externalAuthHandlers.signOut,
  permissions: shouldUseDevFallback() ? getDevFallbackPermissions() : [],
  refreshUser: async () => {
    throw new Error('AuthProvider is not mounted yet. Ensure AuthProvider wraps your component tree.');
  },
  reloadPermissions: async () => {
    throw new Error('AuthProvider is not mounted yet. Ensure AuthProvider wraps your component tree.');
  },
  reauthenticateWithPassword: async () => {
    throw new Error('AuthProvider is not mounted yet. Ensure AuthProvider wraps your component tree.');
  },
});

export const AuthProvider = ({ children }) => {
  const e2ePreset = isDevEnvironment ? getE2EPreset() : null;
  const completedSignupTelemetryKeysRef = useRef(new Set());
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(shouldUseDevFallback() ? getFallbackUser() : null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState(
    shouldUseDevFallback()
      ? [...(e2ePreset?.permissions || FALLBACK_PERMISSIONS)]
      : [],
  );

  useEffect(() => {
    clearRetiredAdminPreviewState();
  }, []);

  const syncUserProfileSnapshot = useCallback(
    async (profile) => {
      if (!profile?.id) {
        return profile;
      }
      const hydrated = await hydrateUserProfileSnapshot(profile);
      if (hydrated !== profile) {
        setUser((current) => (current?.id === hydrated.id ? hydrated : current));
      }
      return hydrated;
    },
    [setUser],
  );

  const applySession = useCallback((nextSession) => {
    setSession(nextSession);
    if (isDevEnvironment && typeof window !== 'undefined') {
      try { window.__E2E_AUTH_SESSION__ = nextSession;
      } catch (assignError) {
        console.warn('[AuthContext] Unable to expose E2E session hook', assignError);
      }
    }
    if (nextSession?.user) {
      const mappedUser = mapSupabaseUserToProfile(nextSession.user);
      setUser(mappedUser);
      syncUserProfileSnapshot(mappedUser);
    } else if (shouldUseDevFallback()) {
      setUser(getFallbackUser());
      setPermissions(getDevFallbackPermissions());
    } else {
      setUser(null);
      setPermissions([]);
    }
  }, [syncUserProfileSnapshot]);

  const loadPermissions = useCallback(async () => {
    try {
      const { data, error: permissionsError } = await supabase.rpc('list_user_permissions');
      if (permissionsError) {
        throw permissionsError;
      }

      const nextPermissions = Array.isArray(data)
        ? data
            .map((row) =>
              typeof row === 'string' ? row : row?.permission,
            )
            .filter((value) => typeof value === 'string' && value.length > 0)
        : [];

      setPermissions(nextPermissions);
      return nextPermissions;
    } catch (permissionsError) {
      console.warn('[AuthContext] loadPermissions failed', permissionsError);
      const fallback = shouldUseDevFallback() ? getDevFallbackPermissions() : [];
      setPermissions(fallback);
      return fallback;
    }
  }, []);

  const exchangeSessionFromUrl = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }
    const url = new URL(window.location.href);
    const queryCode = url.searchParams.get('code') || '';
    const hasQueryCode = queryCode.length > 0;
    const hashFragment = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
    const hashParams = new URLSearchParams(hashFragment);
    const hashAccessToken = hashParams.get('access_token') || '';
    const hashRefreshToken = hashParams.get('refresh_token') || '';
    const hashProviderToken = hashParams.get('provider_token') || '';
    const hasHashToken = Boolean(hashAccessToken || hashRefreshToken || hashProviderToken);
    const isRecoveryFlow =
      hashParams.get('type') === 'recovery' || url.searchParams.get('type') === 'recovery';
    const isRecoveryPath = ['/reset-password', STAFF_RESET_PATH].includes(window.location.pathname);

    if (isRecoveryFlow && !isRecoveryPath) {
      const nextUrl = `/reset-password${url.search}${url.hash}`;
      window.location.replace(nextUrl);
      return;
    }

    // Prevent noisy PKCE exchange attempts when URL params exist but are empty (e.g. `?code=`).
    if (!hasQueryCode && url.searchParams.has('code')) {
      url.searchParams.delete('code');
      const cleanedUrl = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, document.title, cleanedUrl || url.pathname);
    }

    if (!hasQueryCode && !hasHashToken) {
      return;
    }

    // Hash-based magic-link sessions are restored by the Supabase client itself.
    // Forcing a PKCE code exchange here produces noisy `code_verifier` errors.
    if (!hasQueryCode) {
      return;
    }

    const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(queryCode);
    if (exchangeError) {
      console.error('[AuthContext] exchangeCodeForSession error:', exchangeError);
      setError(exchangeError);
      const exchangeMessage = (exchangeError?.message || String(exchangeError)).toLowerCase();
      const shouldClearAuthCode = exchangeMessage.includes('code_verifier');
      if (shouldClearAuthCode) {
        if (hasQueryCode) {
          url.search = '';
        }
        if (hasHashToken) {
          url.hash = '';
        }
        const cleanedUrl = `${url.pathname}${url.search}${url.hash}`;
        window.history.replaceState({}, document.title, cleanedUrl || url.pathname);
      }
      return;
    }

    if (data?.session) {
      applySession(data.session);
    }

    if (hasQueryCode) {
      url.search = '';
    }
    if (hasHashToken) {
      url.hash = '';
    }
    const cleanedUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, document.title, cleanedUrl || url.pathname);

    if (isRecoveryFlow && !isRecoveryPath) {
      window.location.replace('/reset-password');
    }
  }, [applySession]);

  useEffect(() => {
    let isActive = true;

    const fetchInitialSession = async () => {
      setIsLoading(true);
      const preset = getE2EPreset();
      try {
        await exchangeSessionFromUrl();
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (!isActive) return;

        let nextSession = data?.session || null;

        if (sessionError) {
          console.warn('Supabase getSession error:', sessionError);
          setError(sessionError);
          nextSession = null;
        } else {
          setError(null);
        }

        // Supabase restores sessions from localStorage, so role/metadata can be stale
        // after server-side changes (e.g., admin demotion). Refresh the user once on boot
        // so RBAC reflects the canonical auth.users metadata without requiring a re-login.
        if (nextSession?.access_token) {
          try {
            const { data: userData, error: userError } = await supabase.auth.getUser(
              nextSession.access_token,
            );
            if (!userError && userData?.user) {
              nextSession = {
                ...nextSession,
                user: userData.user,
              };
            }
          } catch (userError) {
            console.warn('[AuthContext] boot getUser failed', userError);
          }
        }

        if (!nextSession && preset?.accessToken) {
          nextSession = {
            access_token: preset.accessToken,
            token_type: 'bearer',
            expires_in: 3600,
            refresh_token: null,
            provider_token: null,
            user: null,
          };
          setError(null);
        }

        applySession(nextSession);
      } catch (sessionException) {
        console.warn('Supabase getSession exception:', sessionException);
        if (preset?.accessToken) {
          applySession({
            access_token: preset.accessToken,
            token_type: 'bearer',
            expires_in: 3600,
            refresh_token: null,
            provider_token: null,
            user: null,
          });
          setError(null);
        } else {
          setError(sessionException);
          applySession(null);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    fetchInitialSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!isActive) return;
      applySession(nextSession || null);
      setError(null);
      setIsLoading(false);
    });

    return () => {
      isActive = false;
      subscription?.unsubscribe();
    };
  }, [applySession, exchangeSessionFromUrl]);

  const linkIdentity = useCallback(
    async (provider) => {
      if (!provider || typeof provider !== 'string') {
        throw new Error('link_identity_invalid_provider');
      }
      if (!session?.user) {
        throw new Error('link_identity_requires_session');
      }
      setIsLoading(true);
      setError(null);
      try {
        await assertIdentityLinkSession(supabase, session.user.id);
        const { data, error: linkError } = await supabase.auth.linkIdentity({
          provider,
          options: {
            redirectTo: `${window.location.origin}/auth/callback?provider=${provider}`,
          },
        });
        if (linkError) {
          throw linkError;
        }
        return data;
      } catch (linkError) {
        console.error('[AuthContext] linkIdentity failed', linkError);
        setError(linkError);
        throw linkError;
      } finally {
        setIsLoading(false);
      }
    },
    [session?.user],
  );

  const signIn = useCallback(
    async (credentials) => {
      setIsLoading(true);
      setError(null);

      const providerLabel = getProviderLabel(credentials);

      try {
        let response;

        if (credentials?.provider) {
          const { provider, options, ...rest } = credentials;
          response = await supabase.auth.signInWithOAuth({ provider, options });
          if (Object.keys(rest).length > 0) {
            console.warn('Unused credentials passed to OAuth signIn:', rest);
          }
        } else {
          response = await supabase.auth.signInWithPassword(credentials);
        }

        if (response?.error) {
          throw response.error;
        }

        if (response?.data?.session) {
          applySession(response.data.session);
          await loadPermissions();
          void sendSignInTelemetry({
            status: 'success',
            provider: providerLabel,
            accessToken: response.data.session?.access_token || null,
          });
        } else if (response?.data?.user) {
          const mappedUser = mapSupabaseUserToProfile(response.data.user);
          setUser(mappedUser);
          await syncUserProfileSnapshot(mappedUser);
          await loadPermissions();
          const currentSession = await supabase.auth.getSession();
          const accessToken = currentSession?.data?.session?.access_token || null;
          void sendSignInTelemetry({
            status: 'success',
            provider: providerLabel,
            accessToken,
          });
        }

        return response?.data || null;
      } catch (signInError) {
        console.error('Supabase signIn error:', signInError);
        setError(signInError);
        try {
          const emailHash = await hashEmailForTelemetry(credentials?.email);
          void sendSignInTelemetry({
            status: 'failure',
            provider: providerLabel,
            emailHash,
            reason: signInError?.message || signInError?.name || 'unknown_error',
          });
        } catch (telemetryError) {
          console.warn('[AuthContext] Unable to queue failure telemetry', telemetryError);
        }
        if (shouldUseDevFallback()) {
          setUser(getFallbackUser());
          setPermissions(getDevFallbackPermissions());
        }
        throw signInError;
      } finally {
        setIsLoading(false);
      }
    },
    [applySession, loadPermissions, syncUserProfileSnapshot]
  );

  const recordAuthSignupCompleted = useCallback(async ({
    provider = 'email',
    session: completedSession = null,
  } = {}) => {
    if (!isBrowserEnvironment || provider !== 'email') {
      return { attempted: false, reason: 'ineligible_signup_method' };
    }

    const accessToken = completedSession?.access_token || null;
    if (!accessToken) {
      return { attempted: false, reason: 'authenticated_session_missing' };
    }

    let consent;
    try {
      const storedConsent = window.localStorage?.getItem('gs_cookie_consent');
      consent = storedConsent ? JSON.parse(storedConsent) : null;
    } catch {
      return { attempted: false, reason: 'consent_unreadable' };
    }

    const consentAnalytics = Boolean(consent?.categories?.analytics);
    const consentMarketing = Boolean(consent?.categories?.marketing);
    const consentSessionId = consent?.sessionId || null;
    const consentReceiptId = consent?.receiptId || null;
    if (!consentAnalytics || !consentSessionId || !consentReceiptId) {
      return { attempted: false, reason: 'analytics_consent_not_applicable' };
    }

    const authenticatedSubject = completedSession?.user?.id || accessToken;
    const deliveryKey = `${provider}:${authenticatedSubject}`;
    if (completedSignupTelemetryKeysRef.current.has(deliveryKey)) {
      return { attempted: false, reason: 'already_attempted' };
    }
    completedSignupTelemetryKeysRef.current.add(deliveryKey);

    try {
      const response = await fetch('/api/telemetry/auth-signup', {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          provider,
          consent: {
            analytics: consentAnalytics,
            marketing: consentMarketing,
            sessionId: consentSessionId,
            receiptId: consentReceiptId,
          },
        }),
      });

      if (!response.ok) {
        console.warn('[AuthContext] telemetry signup was not accepted');
      }

      return { attempted: true, delivered: response.ok };
    } catch (telemetryError) {
      console.warn('[AuthContext] telemetry signup failed', telemetryError);
      return { attempted: true, delivered: false };
    }
  }, []);

  const signUp = useCallback(
    async (credentials = {}) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await signUpWithPublicEmail(credentials);

        let authenticatedSession = response?.session || null;
        if (authenticatedSession) {
          const { data: sessionData, error: setSessionError } =
            await supabase.auth.setSession({
              access_token: authenticatedSession.access_token,
              refresh_token: authenticatedSession.refresh_token,
            });
          if (setSessionError) {
            throw setSessionError;
          }
          authenticatedSession = sessionData?.session || authenticatedSession;
          applySession(authenticatedSession);
          await loadPermissions();
        }

        void recordAuthSignupCompleted({
          provider: 'email',
          session: authenticatedSession,
        });

        return response;
      } catch (signUpError) {
        console.error('Supabase signUp error:', signUpError);
        setError(signUpError);
        if (shouldUseDevFallback()) {
          setUser(getFallbackUser());
          setPermissions(getDevFallbackPermissions());
        }
        throw signUpError;
      } finally {
        setIsLoading(false);
      }
    },
    [applySession, loadPermissions, recordAuthSignupCompleted]
  );

  const signOut = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        throw signOutError;
      }
      applySession(null);
    } catch (signOutError) {
      console.error('Supabase signOut error:', signOutError);
      setError(signOutError);
      throw signOutError;
    } finally {
      clearRetiredAdminPreviewState();
      setIsLoading(false);
    }
  }, [applySession]);

  const reauthenticateWithPassword = useCallback(
    async (password) => {
      const email = typeof user?.email === 'string' ? user.email.trim() : '';
      const normalizedPassword = String(password || '').trim();

      if (!email || !normalizedPassword) {
        const error = new Error('Confirm your password to continue.');
        error.code = 'missing_reauthentication_password';
        error.status = 400;
        throw error;
      }

      const { data, error: reauthError } = await supabase.auth.signInWithPassword({
        email,
        password: normalizedPassword,
      });

      if (reauthError || !data?.session) {
        const error = reauthError || new Error('invalid_reauthentication');
        error.code = 'invalid_reauthentication';
        error.status = 403;
        throw error;
      }

      applySession(data.session);
      await loadPermissions();
      return data.session;
    },
    [applySession, loadPermissions, user?.email],
  );

  const refreshUser = useCallback(async () => {
    try {
      const { data, error: getUserError } = await supabase.auth.getUser();
      if (getUserError) {
        throw getUserError;
      }
      const mappedUser = mapSupabaseUserToProfile(data?.user);
      if (mappedUser) {
        setUser(mappedUser);
        const hydratedUser = await syncUserProfileSnapshot(mappedUser);
        await loadPermissions();
        return hydratedUser;
      }
      if (shouldUseDevFallback()) {
        const fallbackUser = getFallbackUser();
        setUser(fallbackUser);
        setPermissions(getDevFallbackPermissions());
        return fallbackUser;
      }
      setUser(null);
      setPermissions([]);
      return null;
    } catch (refreshError) {
      console.error('Supabase refreshUser error:', refreshError);
      setError(refreshError);
      if (shouldUseDevFallback()) {
        const fallbackUser = getFallbackUser();
        setUser(fallbackUser);
        setPermissions(getDevFallbackPermissions());
        return fallbackUser;
      }
      setPermissions([]);
      throw refreshError;
    }
  }, [loadPermissions, syncUserProfileSnapshot]);

  useEffect(() => {
    setExternalAuthHandlers({ signIn, signUp, signOut });
  }, [signIn, signUp, signOut]);

  useEffect(() => {
    if (!session?.user?.id) {
      return;
    }

    loadPermissions();
  }, [session?.user?.id, loadPermissions]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (!session?.access_token) {
      return undefined;
    }

    let idleTimer;

    const handleIdleLogout = async () => {
      try {
        await signOut();
      } catch (idleError) {
        console.error('[AuthContext] idle logout failed', idleError);
        setError(idleError);
      }
    };

    const resetTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        handleIdleLogout();
      }, IDLE_TIMEOUT_MS);
    };

    const activityEvents = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'visibilitychange',
    ];

    const handleActivity = (event) => {
      if (event.type === 'visibilitychange' && document.visibilityState !== 'visible') {
        return;
      }
      resetTimer();
    };

    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, handleActivity, { passive: true }),
    );

    resetTimer();

    return () => {
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, handleActivity),
      );
      clearTimeout(idleTimer);
    };
  }, [session?.access_token, signOut]);


  const controlledUser = applyE2EOverrides(user);

  const value = useMemo(
    () => ({
      session,
      user: controlledUser,
      permissions,
      isLoading,
      error,
      signIn,
      signUp,
      recordAuthSignupCompleted,
      signOut,
      refreshUser,
      reloadPermissions: loadPermissions,
      reauthenticateWithPassword,
      linkIdentity,
      idleTimeoutMinutes: IDLE_TIMEOUT_MINUTES,
    }),
    [
      session,
      controlledUser,
      permissions,
      isLoading,
      error,
      signIn,
      signUp,
      recordAuthSignupCompleted,
      signOut,
      refreshUser,
      loadPermissions,
      reauthenticateWithPassword,
      linkIdentity,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
